'use client'

import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'

import { IconChevLeft, IconChevRight } from '@/app/components/atoms/Icons'
import { cx } from '@/app/lib/utils'

import styles from './Calendar.module.scss'
import type { CalendarProps } from './Calendar.types'
import { isDateInRange, isInRange, isRangeEndpoint, isSameDay, stripTime, toIsoDate } from './utils/dateRange'

const LOCALE = 'es-ES'
const WEEKDAY_LABELS = ['L', 'M', 'X', 'J', 'V', 'S', 'D']
// Array propio (no `Intl.DateTimeFormat({month:'long', year:'numeric'})`, que en
// es-ES inserta "de" — "julio de 2026"): el título del mes debe leerse "Julio
// 2026", sin "de" (fiel al diseño de referencia, que usa el mismo array propio).
const MONTH_FULL = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
]
const DAY_LABEL_FORMATTER = new Intl.DateTimeFormat(LOCALE, {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
})

function capitalize(text: string): string {
  return text.length === 0 ? text : text.charAt(0).toUpperCase() + text.slice(1)
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

function addMonths(date: Date, amount: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + amount, 1)
}

function addDays(date: Date, amount: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount)
}

/** Lunes=0 … Domingo=6. */
function isoWeekday(date: Date): number {
  return (date.getDay() + 6) % 7
}

/** Grilla de un mes: `null` para las celdas vacías antes del día 1 y tras el
 * último día (se completa hasta múltiplo de 7) — fiel al diseño de referencia:
 * la altura de la grilla varía por mes (5 o 6 filas), no siempre 6 semanas. */
function buildMonthGrid(monthStart: Date): Array<Date | null> {
  const daysInMonth = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0).getDate()
  const cells: Array<Date | null> = []
  for (let i = 0; i < isoWeekday(monthStart); i += 1) cells.push(null)
  for (let day = 1; day <= daysInMonth; day += 1) cells.push(new Date(monthStart.getFullYear(), monthStart.getMonth(), day))
  while (cells.length % 7 !== 0) cells.push(null)
  return cells
}

const ARROW_KEY_DELTA: Record<string, number> = {
  ArrowLeft: -1,
  ArrowRight: 1,
  ArrowUp: -7,
  ArrowDown: 7,
}

/**
 * `Calendar` — selector de día en formato mes/grilla, restringido a `[min, max]`
 * (FR-006), con selección de una única fecha o de un rango de dos (`DateRange`,
 * ver `dateRange.ts` — fiel al diseño de referencia, `eci-firefly-uikit`,
 * `AgendaDatePicker`). Componente compartido y reutilizable (Atomic Design
 * `shared/` — Principio III), sin dependencia de ninguna librería de fechas:
 * solo `Date`/`Intl` nativos. No fija su propio fondo/superficie — usa tokens
 * semánticos (`--color-text-primary`, etc.) para que el contenedor que lo aloje
 * (p. ej. el popover oscuro de `DatePicker`) pueda redefinirlos en cascada, igual
 * que hace el diseño de referencia. Navegación por teclado con foco itinerante
 * (roving tabindex): flechas mueven el foco entre días, `Enter`/`click` selecciona.
 *
 * `viewedMonth`/`focusedDate` solo se inicializan desde `range.start` al montar
 * (no hay efecto de resincronización): este componente vive siempre detrás de
 * un `{open && <Calendar />}` condicional en `DatePicker`, así que se
 * desmonta/remonta entero cada vez que el popover se cierra/abre, y el mes
 * mostrado al reabrirlo ya refleja el `range` vigente en ese momento.
 */
export function Calendar({
  range,
  min,
  max,
  onSelect,
  hasSelection = true,
  label = 'Elige un día',
  className,
}: CalendarProps) {
  const [viewedMonth, setViewedMonth] = useState(() => startOfMonth(range.start))
  const [focusedDate, setFocusedDate] = useState(() => range.start)
  const buttonRefs = useRef(new Map<string, HTMLButtonElement>())
  const isFirstRenderRef = useRef(true)

  const cells = useMemo(() => buildMonthGrid(viewedMonth), [viewedMonth])
  const today = useMemo(() => stripTime(new Date()), [])

  const canGoPrev = startOfMonth(viewedMonth).getTime() > startOfMonth(min).getTime()
  const canGoNext = startOfMonth(viewedMonth).getTime() < startOfMonth(max).getTime()

  // Solo mueve el foco real del navegador cuando `focusedDate` cambia por
  // navegación de teclado dentro de la grilla (nunca en el montaje inicial, para
  // no robar el foco de la página al cargarla — los botones de mes no tocan
  // `focusedDate`, solo `viewedMonth`).
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false
      return
    }
    buttonRefs.current.get(toIsoDate(focusedDate))?.focus()
  }, [focusedDate])

  function selectDate(date: Date) {
    if (!isDateInRange(date, min, max)) return
    setViewedMonth(startOfMonth(date))
    setFocusedDate(date)
    onSelect(date)
  }

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>, date: Date) {
    const delta = ARROW_KEY_DELTA[event.key]
    if (delta === undefined) return
    event.preventDefault()
    const next = addDays(date, delta)
    if (!isDateInRange(next, min, max)) return
    if (startOfMonth(next).getTime() !== startOfMonth(viewedMonth).getTime()) setViewedMonth(startOfMonth(next))
    setFocusedDate(next)
  }

  return (
    <div className={cx(styles.root, className)}>
      <div className={styles.header}>
        <button
          type="button"
          onClick={() => canGoPrev && setViewedMonth(addMonths(viewedMonth, -1))}
          disabled={!canGoPrev}
          aria-label="Mes anterior"
          className={styles.navButton}
        >
          <IconChevLeft size={16} />
        </button>
        <p className={styles.monthLabel} aria-live="polite">
          {MONTH_FULL[viewedMonth.getMonth()]} {viewedMonth.getFullYear()}
        </p>
        <button
          type="button"
          onClick={() => canGoNext && setViewedMonth(addMonths(viewedMonth, 1))}
          disabled={!canGoNext}
          aria-label="Mes siguiente"
          className={styles.navButton}
        >
          <IconChevRight size={16} />
        </button>
      </div>

      <div className={styles.weekdays} aria-hidden="true">
        {WEEKDAY_LABELS.map((weekday) => (
          <span key={weekday} className={styles.weekday}>
            {weekday}
          </span>
        ))}
      </div>

      <div className={styles.grid} role="group" aria-label={label}>
        {cells.map((date, index) => {
          if (!date) return <span key={`empty-${index}`} className={styles.empty} aria-hidden="true" />

          const inBounds = isDateInRange(date, min, max)
          const isEndpoint = hasSelection && isRangeEndpoint(date, range)
          const inSelectedRange = hasSelection && isInRange(date, range)
          const isToday = isSameDay(date, today)
          const isFocusTarget = isSameDay(date, focusedDate)
          const isoDate = toIsoDate(date)

          return (
            <button
              key={isoDate}
              type="button"
              ref={(el) => {
                if (el) buttonRefs.current.set(isoDate, el)
                else buttonRefs.current.delete(isoDate)
              }}
              tabIndex={isFocusTarget ? 0 : -1}
              disabled={!inBounds}
              aria-current={isToday ? 'date' : undefined}
              aria-pressed={isEndpoint}
              aria-label={capitalize(DAY_LABEL_FORMATTER.format(date))}
              className={cx(
                styles.day,
                isEndpoint && styles.daySelected,
                inSelectedRange && styles.dayInRange,
                isToday && !isEndpoint && styles.dayToday,
              )}
              onClick={() => selectDate(date)}
              onKeyDown={(event) => handleKeyDown(event, date)}
            >
              {date.getDate()}
            </button>
          )
        })}
      </div>
    </div>
  )
}
